/**
 * Figures the Committee panel prints about the selected deal.
 *
 * The panel's "Cap Rate" read `market_data.capRate`, a blob with no named
 * source: a legacy promotion copied the package's own cap rate there (as a
 * fraction, so it printed "0.07%"), and Calloway Yards' demo blob carries 5.15
 * against the deal's 4.79%. The Economics tab already refuses a market cap
 * rate without a named source; here the figure is the deal's own cap rate.
 */
type CapRateSource = {
  customFields?: Record<string, unknown>
  market?: { capRate?: unknown } | null
}

export function committeeCapRate(deal: CapRateSource): string {
  const raw = deal.customFields?.capRate
  if (typeof raw !== 'number' || !Number.isFinite(raw) || raw <= 0) return '—'
  const pct = raw <= 1.5 ? raw * 100 : raw
  return `${pct.toFixed(2)}%`
}

type DebtYieldSource = {
  debtYield?: unknown
  customFields?: Record<string, unknown>
  derivedFields?: string[]
}

/**
 * The deal's debt yield. Live deals keep it in the catalogue
 * (`custom_fields.debt_yield`, 0-100); the strip read only the top-level
 * `debtYield` the curated book carries and printed "—" for Calloway's 6.9%
 * while the memo on the same screen quoted it (nd-006). A value the platform
 * derived says so.
 */
export function committeeDebtYield(deal: DebtYieldSource): string {
  const top = deal.debtYield
  const raw = typeof top === 'number' && Number.isFinite(top) && top > 0 ? top : deal.customFields?.debtYield
  if (typeof raw !== 'number' || !Number.isFinite(raw) || raw <= 0) return '—'
  const pct = raw <= 1.5 ? raw * 100 : raw
  const derived = (deal.derivedFields ?? []).includes('debt_yield') ? ' · derived' : ''
  return `${pct.toFixed(2)}%${derived}`
}

/**
 * The committee date a memo is scheduled for. A memo created without one
 * stores null, and the page built `new Date(null + 'T00:00:00')`, which printed
 * "Invalid Date" in the queue and the detail panel. A bare calendar date is
 * read as that local day; a full timestamp is parsed as written.
 */
export function committeeDateLabel(iso: string | null | undefined): string {
  const raw = typeof iso === 'string' ? iso.trim() : ''
  if (!raw) return 'Not scheduled'
  const d = new Date(/^\d{4}-\d{2}-\d{2}$/.test(raw) ? `${raw}T00:00:00` : raw)
  if (Number.isNaN(d.getTime())) return raw
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}
